import { Grid2, Box, Divider } from '@mui/material';
import CustomButton from '@ui-components/Button/CustomButton';
import CustomCard from '@ui-components/Card/CustomCard';
import CustomCardContent from '@ui-components/Card/CustomCardContent';
import CustomChip from '@ui-components/Chip/CustomChip';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import React, { useState } from 'react';
import { CallStatusEnum } from './shared/CallStatusEnum';
import type { CustomChipColor } from '@ui-components/Chip/CustomChip'; // Ensure CustomChipColor is imported

import VideoPlayIcon from '@assets/images/Video.svg';

import MinutesTimer from '../components/MinutesTimer';
import RestartConfirmDialog from '../components/RestartConfirmDialog';

//CSS
import './myconsults.css';
import CustomLink from '@ui-components/Link/CustomLink';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';

//Icons
import ChatIcon from '@assets/images/chat.svg';
import VectorIcon from '@assets/images/Vector.svg';
import NewIcon from '@assets/images/newIcon.svg';
import InProgressIcon from '@assets/images/inProgress.svg';
import CallStartedIcon from '@assets/images/callStarted.svg';
import CallCompletedIcon from '@assets/images/callCompleted.svg';
import CompletedIcon from '@assets/images/completed.svg';
import CancelledIcon from '@assets/images/cancelled.svg';
import AscensionLogo from '@assets/images/ascension_logo.svg';

import type { ConsultsResponse } from '../../API/services/consultsService';
import CustomTooltip from '@ui-components/ToolTip/CustomToolTip';
import { formatName } from '../components/DisplayName';
import { useNavigate } from 'react-router-dom';
import { useStartZoomMeeting } from '../../shared/zoom/zoomSetup';
import moment from 'moment';
import { decryptText } from '../healper/decryption';
import CustomBadge from '@qler-view/shared_lib/src/shared/lib/ui-components/Badge/CustomBadge';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src';
import { formatFieldValue } from '@qler-view/shared_lib/src/reusableFunctions/CommonFunctions';
import TimeTicker from '@qler-view/shared_lib/src/shared/reusableComponents/TimeTicker';
import TimeDifference from '@qler-view/shared_lib/src/shared/reusableComponents/TimeDifference';

interface ConsultsProps {
  consultData: ConsultsResponse;
  assigned: boolean;
  onAccept?: (data: ConsultsResponse) => void; // Callback function
  isAssigned: boolean;
  pastConsult?: boolean;
}

const getChipDetails = (status: CallStatusEnum) => {
  switch (status) {
    case CallStatusEnum.New:
      return { color: 'bg-New', icon: <NewIcon /> };
    case CallStatusEnum.InProgress:
      return { color: 'bg-InProgress', icon: <InProgressIcon /> };
    case CallStatusEnum.CallStarted:
      return { color: 'bg-CallStarted', icon: <CallStartedIcon /> };
    case CallStatusEnum.CallCompleted:
      return { color: 'bg-CallCompleted', icon: <CallCompletedIcon /> };
    case CallStatusEnum.Completed:
      return { color: 'bg-Completed', icon: <CompletedIcon /> };
    case CallStatusEnum.Cancelled:
      return { color: 'bg-Cancelled', icon: <CancelledIcon /> };
    default:
      return { color: 'default', icon: <NewIcon /> };
  }
};

const callStatusToString: { [key in CallStatusEnum]: string } = {
  [CallStatusEnum.New]: 'New',
  [CallStatusEnum.InProgress]: 'In Progress',
  [CallStatusEnum.CallStarted]: 'Call Started',
  [CallStatusEnum.CallCompleted]: 'Call Completed',
  [CallStatusEnum.Completed]: 'Completed',
  [CallStatusEnum.Cancelled]: 'Cancelled',
  [CallStatusEnum.Abandoned]: 'Abandoned',
};

const getButtonLabel = (sessionStatus: string | null): string => {
  if (!sessionStatus) return '';

  switch (sessionStatus.toLowerCase()) {
    case CallStatusEnum.New:
      return 'Accept';
    case CallStatusEnum.InProgress:
      return 'Start';
    case CallStatusEnum.CallStarted:
      return 'Restart';
    default:
      return '';
  }
};

const CheckFOrStatusNew = (status?: string | null) => {
  if (status?.toLocaleLowerCase() === CallStatusEnum.New) {
    return true;
  }
  return false;
};

const getValueOrNA = (value: string | null) => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }
  return value;
};

const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '…';
};

const getResponsiveMaxLength = () => {
  const width = window.innerWidth;

  if (width < 600) return 30; // XS: mobile portrait
  if (width < 900) return 40; // SM: large mobile, small tablets
  if (width < 1200) return 50; // MD: tablets landscape, small laptops
  if (width < 1536) return 50; // LG: desktops
  return 60; // XL: large desktop monitors
};

const ConsultCard: React.FC<ConsultsProps> = ({
  consultData,
  assigned,
  onAccept,
  isAssigned,
  pastConsult,
}) => {
  const navigate = useNavigate();
  const consultStatus = consultData.sessionStatus?.toLowerCase();

  const { color: chipColor, icon: chipIcon } = getChipDetails(
    consultStatus?.toString() as CallStatusEnum
  );

  const { startZoomMeeting } = useStartZoomMeeting(PORTAL_TYPES.CLINICAL);

  const newWaitTime = CheckFOrStatusNew(consultData.sessionStatus);

  const [openRestartConfirm, setOpenRestartConfirm] = useState(false);
  const handleRestartOpen = () => setOpenRestartConfirm(true);
  const handleRestartClose = () => setOpenRestartConfirm(false);

  const handleRestartConfirm = () => {
    setOpenRestartConfirm(false);
    startZoomMeeting(
      Number.parseInt(localStorage.getItem('userId') ?? '0'),
      consultData.consultId,
      localStorage.getItem('username') ?? 'Clinician'
    );
  };

  const getCardHeader = () => {
    return (
      <Grid2 container>
        <Grid2 size={{ xs: 7, md: 7, lg: 7.4, xl: 7.2 }}>
          <Box className="text-align-left pb-4">
            <CustomTooltip
              id="name-tooltip"
              title={consultData.partnerName?.toString()}
              placement="bottom-end"
            >
              <CustomTypography
                variant="body3"
                id="HealthSystemName"
                color="textSecondary"
                noWrap={true}
              >
                {getValueOrNA(consultData.partnerName)}
              </CustomTypography>
            </CustomTooltip>
          </Box>
          <Box className="text-align-left">
            <CustomTooltip
              id="facilityname-tooltip"
              title={consultData.facilityName?.toString()}
              placement="bottom-end"
            >
              <CustomTypography variant="h5" id="FacilityName" noWrap={true}>
                {getValueOrNA(consultData.facilityName)}
              </CustomTypography>
            </CustomTooltip>
          </Box>
        </Grid2>
        <Grid2
          size={{ xs: 5, md: 5, lg: 4.6, xl: 4.8 }}
          className={`text-align-end ${pastConsult || newWaitTime ? 'display-timer-pastconsult' : ''}`}
        >
          <Box className="display-grid">
            <Box className="flex justify-end width-100">
              {consultData?.isExternalConsult !== null &&
                consultData?.isExternalConsult && (
                  <Box className="pr-8">
                    <AscensionLogo />
                  </Box>
                )}
              {consultData.sessionStatus?.toLowerCase() ===
                CallStatusEnum.New ||
              consultData.sessionStatus?.toLowerCase() ===
                CallStatusEnum.InProgress ? (
                <Box
                  className={`flex clock-text ${!pastConsult && !newWaitTime ? 'pb-4' : ''}`}
                >
                  <TimeTicker createdOn={consultData.waitStartTime} />
                </Box>
              ) : (
                <Box
                  className={`flex clock-text ${!pastConsult && !newWaitTime ? 'pb-4' : ''}`}
                >
                  <TimeDifference
                    createdOn={consultData.waitStartTime ?? ''}
                    waitEndTime={consultData.waitEndTime ?? ''}
                  />
                </Box>
              )}
            </Box>
            {!isAssigned && !pastConsult && (
              <Box className="float-right">
                {consultStatus !== CallStatusEnum.New && (
                  <CustomTypography variant="body3" id="assignedminsago">
                    <MinutesTimer targetTime={consultData.acceptedDateTime} />
                  </CustomTypography>
                )}
              </Box>
            )}
          </Box>
        </Grid2>
      </Grid2>
    );
  };

  const formatDecryptedValue = (encryptedValue: string) => {
    if (!encryptedValue || encryptedValue === 'N/A') {
      return 'N/A';
    }

    const decryptedValue = decryptText(encryptedValue);

    if (
      !decryptedValue ||
      decryptedValue === 'N/A' ||
      decryptedValue === 'NaN-NaN-NaN'
    ) {
      return 'N/A';
    }

    // Check if the decrypted value is a valid date
    const momentDate = moment(decryptedValue);
    if (
      momentDate.isValid() &&
      !Number.isNaN(Date.parse(decryptedValue)) &&
      decryptedValue.match(/^\d{4}-\d{2}-\d{2}|^\d{4}\/\d{2}\/\d{2}/)
    ) {
      return moment(decryptedValue).format('MM-DD-YYYY');
    }

    return decryptedValue;
  };

  return (
    <CustomCard
      id={`consult-Card-${consultData.consultId}`}
      className="consult-card"
    >
      <CustomCardContent id={''} className="consult-card-container">
        {getCardHeader()}

        <Grid2 container>
          <Grid2 size={{ xs: 12 }}>
            <Box className="pt-18">
              <Divider />
            </Box>
          </Grid2>
        </Grid2>
        <Grid2 container>
          <Grid2 size={{ xs: 12 }}>
            <Box className="text-align-left pt-16 pb-8">
              <CustomTypography variant="h6" id="patientName">
                {formatName(
                  consultData.patientFirstName ?? '',
                  consultData.patientLastName ?? ''
                )}
              </CustomTypography>
            </Box>
          </Grid2>
        </Grid2>
        <Grid2 container>
          <Grid2 size={{ xs: 4 }}>
            <Box
              className="text-align-left flex align-items-center pb-8"
              gap={0.5}
            >
              <CustomTypography
                variant="body3"
                id="dateofbirthTitle"
                color="textSecondary"
              >
                DOB:
              </CustomTypography>
              <CustomTypography
                variant="subtitle2"
                id="dateofbirth"
                color="textPrimary"
              >
                {formatFieldValue(consultData.dob)}
              </CustomTypography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 4 }}>
            <Box
              className="text-align-left align-items-center flex pb-8"
              gap={0.5}
            >
              <CustomTypography
                variant="body3"
                id="mrnTitle"
                color="textSecondary"
              >
                MRN:
              </CustomTypography>
              <CustomTypography
                variant="subtitle2"
                id="mrnNumber"
                color="textPrimary"
              >
                {formatDecryptedValue(consultData.mrn || 'N/A')}
              </CustomTypography>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 4 }}>
            <Box
              className="text-align-left flex align-items-center pb-8"
              gap={0.5}
            >
              <CustomTypography
                variant="body3"
                id="unitsTitle"
                color="textSecondary"
              >
                ED/IP:
              </CustomTypography>
              <CustomTooltip
                id="Reason-tooltip"
                title={consultData.unitDepartment?.toString()}
                placement="bottom-end"
              >
                <CustomTypography
                  variant="subtitle2"
                  id="units"
                  color="textPrimary"
                >
                  {truncateText(getValueOrNA(consultData.unitDepartment), 20)}
                </CustomTypography>
              </CustomTooltip>
            </Box>
          </Grid2>
        </Grid2>
        <Grid2 container>
          <Grid2 size={{ xs: 6 }}>
            <Box
              className="text-align-left flex align-items-center pb-8"
              gap={0.5}
            >
              <CustomTypography
                variant="body3"
                id="callBackTitle"
                color="textSecondary"
              >
                Call Back:
              </CustomTypography>
              <CustomTooltip
                id="Reason-tooltip"
                title={consultData.callBackNumber?.toString()}
                placement="bottom-end"
              >
                <CustomTypography
                  variant="subtitle2"
                  id="callBackNumber"
                  color="textPrimary"
                >
                  {truncateText(getValueOrNA(consultData.callBackNumber), 20)}
                </CustomTypography>
              </CustomTooltip>
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            <Box
              className="text-align-left flex align-items-center pb-8"
              gap={0.5}
            >
              <CustomTypography
                variant="body3"
                id="cartTitle"
                color="textSecondary"
              >
                Cart:
              </CustomTypography>
              <CustomTypography
                variant="subtitle2"
                id="cart"
                color="textPrimary"
              >
                {getValueOrNA(consultData.cart)}
              </CustomTypography>
            </Box>
          </Grid2>
        </Grid2>
        <Grid2 container>
          <Grid2 size={{ xs: 12 }}>
            <Box
              className="text-align-left align-items-center flex pb-8"
              gap={0.5}
            >
              <CustomTypography
                variant="body3"
                id="reasonTitle"
                color="textSecondary"
              >
                Reason:
              </CustomTypography>
              <CustomTooltip
                id="Reason-tooltip"
                title={consultData.reason?.toString()}
                placement="bottom-end"
              >
                <CustomTypography
                  variant="subtitle2"
                  id="reason"
                  color="textPrimary"
                  noWrap={true}
                >
                  {truncateText(
                    getValueOrNA(consultData.reason),
                    getResponsiveMaxLength()
                  )}
                </CustomTypography>
              </CustomTooltip>
            </Box>
          </Grid2>
        </Grid2>
        <Grid2 container>
          <Grid2 size={{ xs: 12 }}>
            <Box className="flex text-align-left item-center" gap={0.5}>
              <CustomLink
                color="primary"
                underline="hover"
                variant="body3"
                onClick={() => {
                  navigate('/provider/details', {
                    state: {
                      consultId: consultData.consultId,
                      assignedFlag: consultData.assignedFlag,
                      chatOpen: false,
                    },
                  });
                }}
                id="moredetailsLink"
              >
                <span className="display-inline-flex align-items-center cursor-pointer">
                  More Details&nbsp;
                  <VectorIcon />
                </span>
              </CustomLink>
            </Box>
          </Grid2>
        </Grid2>
        <Grid2 container className="align-items-center flex pt-16">
          <Grid2 size={{ xs: 6 }}>
            <Box className="text-align-left" gap={0.5}>
              <CustomChip
                id="statusChip"
                color={chipColor as CustomChipColor}
                className="chip-text-color"
                label={callStatusToString[consultStatus as CallStatusEnum]}
                icon={chipIcon}
                iconPosition="left"
                size="small"
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 6 }}>
            {assigned === false ? (
              <Box
                className="flex"
                justifyContent="flex-end"
                alignItems="center"
              >
                <Box className="icon-background-grey">
                  <CustomBadge
                    id={`${consultData.consultId}-chat-notification`}
                    badgeContent={consultData.unreadMessageCount}
                    color="error"
                    max={99}
                  >
                    <CustomIconButton
                      id="chatIcon"
                      icon={<ChatIcon />}
                      onClick={() =>
                        navigate('/provider/details', {
                          state: {
                            consultId: consultData.consultId,
                            assignedFlag: consultData.assignedFlag,
                            chatOpen: true,
                          },
                        })
                      }
                    ></CustomIconButton>
                  </CustomBadge>
                </Box>
                {consultStatus === CallStatusEnum.InProgress ||
                consultStatus === CallStatusEnum.New ||
                consultStatus === CallStatusEnum.CallStarted ? (
                  <Box className="ml-8">
                    <CustomButton
                      id="btnAction"
                      variant="contained"
                      color={
                        consultStatus === CallStatusEnum.New
                          ? 'success'
                          : 'primary'
                      }
                      startIcon={
                        consultStatus === CallStatusEnum.InProgress ||
                        consultStatus === CallStatusEnum.CallStarted ? (
                          <VideoPlayIcon />
                        ) : null
                      }
                      onClick={() => {
                        if (consultStatus === CallStatusEnum.New) {
                          onAccept?.(consultData);
                        } else if (
                          consultStatus === CallStatusEnum.InProgress
                        ) {
                          startZoomMeeting(
                            Number.parseInt(
                              localStorage.getItem('userId') ?? '0'
                            ),
                            consultData.consultId,
                            localStorage.getItem('username') ?? 'Clinician'
                          );
                        } else if (
                          consultStatus === CallStatusEnum.CallStarted
                        ) {
                          handleRestartOpen();
                        }
                      }}
                      size="large"
                    >
                      {getButtonLabel(consultData.sessionStatus ?? '')}
                    </CustomButton>
                  </Box>
                ) : null}
              </Box>
            ) : (
              <CustomTypography
                variant="h6"
                id="doctorName"
                className="float-right"
              >
                <Box>
                  {`Dr. ${consultData.providerFirstName} ${consultData.providerLastName}`}
                </Box>
              </CustomTypography>
            )}
          </Grid2>
        </Grid2>
      </CustomCardContent>
      <RestartConfirmDialog
        open={openRestartConfirm}
        onClose={handleRestartClose}
        onConfirm={handleRestartConfirm}
      />
    </CustomCard>
  );
};

export default ConsultCard;
